<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

const props = defineProps({
  // The narrow conversation header: the main action shows only its icon, its label goes to aria-label + tooltip.
  iconOnly: { type: Boolean, default: false },
});

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const mainActionBinding = (label, icon) =>
  props.iconOnly ? { icon, 'aria-label': label } : { label };

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = (status, snoozedUntil, customAttributes = null) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
    isLoading.value = false;
  });
};

const actionMenuItems = computed(() => [
  {
    icon: 'i-lucide-alarm-clock-minus',
    label: t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL'),
    action: 'snooze',
    value: 'snooze',
  },
  {
    icon: 'i-lucide-circle-dot-dashed',
    label: t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING'),
    action: 'mark_pending',
    value: 'mark_pending',
  },
]);

const handleMenuAction = ({ action }) => {
  closeDropdown();
  if (action === 'snooze') {
    openSnoozeModal();
  } else if (action === 'mark_pending') {
    toggleStatus(wootConstants.STATUS_TYPE.PENDING);
  }
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  }
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="relative flex items-center resolve-actions">
    <ButtonGroup no-animation class="flex items-center flex-shrink-0">
      <Button
        v-if="isOpen"
        v-tooltip.bottom="
          iconOnly ? t('CONVERSATION.HEADER.RESOLVE_ACTION') : ''
        "
        v-bind="
          mainActionBinding(
            t('CONVERSATION.HEADER.RESOLVE_ACTION'),
            'i-lucide-check'
          )
        "
        size="sm"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        v-tooltip.bottom="
          iconOnly ? t('CONVERSATION.HEADER.REOPEN_ACTION') : ''
        "
        v-bind="
          mainActionBinding(
            t('CONVERSATION.HEADER.REOPEN_ACTION'),
            'i-lucide-rotate-ccw'
          )
        "
        size="sm"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        v-tooltip.bottom="iconOnly ? t('CONVERSATION.HEADER.OPEN_ACTION') : ''"
        v-bind="
          mainActionBinding(
            t('CONVERSATION.HEADER.OPEN_ACTION'),
            'i-lucide-rotate-ccw'
          )
        "
        size="sm"
        no-animation
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <div
        v-if="showAdditionalActions"
        v-on-clickaway="closeDropdown"
        class="relative flex items-center"
      >
        <Button
          ref="arrowDownButtonRef"
          v-tooltip.bottom="t('CONVERSATION.HEADER.STATUS_OPTIONS')"
          icon="i-lucide-chevron-down"
          :aria-label="t('CONVERSATION.HEADER.STATUS_OPTIONS')"
          :disabled="isLoading"
          size="sm"
          no-animation
          class="ltr:rounded-l-none rtl:rounded-r-none"
          @click="openDropdown"
        />
        <DropdownMenu
          v-if="showActionsDropdown"
          :menu-items="actionMenuItems"
          class="mt-1 ltr:right-0 rtl:left-0 top-full"
          @action="handleMenuAction"
        />
      </div>
    </ButtonGroup>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
